/**
 * Form Validation Utilities
 * Provides frontend validation for all forms with field-level error messages
 */

// Maximum amount allowed: 50 crores = 500,000,000
const MAX_AMOUNT = 500000000;

/**
 * Validate donation/pledge form
 * @param {Object} formData - Form data to validate
 * @param {string} pledgeType - 'one_time' or 'recurring'
 * @returns {Object} errors - Object with field names as keys and error messages as values
 */
export const validateDonationForm = (formData, pledgeType = 'recurring', collected_amount = 0) => {
  const errors = {};

  // Contact validation
  if (!formData.contact_id) {
    errors.contact_id = 'Donor is required';
  }

  // Amount validation
  if (!formData.amount) {
    errors.amount = 'Amount is required';
  } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
    errors.amount = 'Amount must be a positive number';
  } else if (!Number.isInteger(parseFloat(formData.amount))) {
    errors.amount = 'Amount must be a whole number (no decimals)';
  } else if (parseFloat(formData.amount) > MAX_AMOUNT) {
    errors.amount = `Amount cannot exceed ₹50 crores (₹${MAX_AMOUNT.toLocaleString('en-IN')})`;
  }

  // Donation schema validation
  if (!formData.donation_schema) {
    errors.donation_schema = 'Donation scheme is required';
  }

  // Trust Master validation
  if (!formData.trust_master_id) {
    errors.trust_master_id = 'Trust is required';
  }

  // Pledge start date validation
  if (!formData.pledge_start_date) {
    errors.pledge_start_date = 'Start date is required';
  }

  // Recurring-specific validations
  if (pledgeType === 'recurring') {
    // Frequency validation
    if (!formData.frequency) {
      errors.frequency = 'Frequency is required for recurring donations';
    }

    // Pledge end date validation
    if (!formData.pledge_end_date) {
      errors.pledge_end_date = 'End date is required for recurring donations';
    } else if (formData.pledge_start_date && formData.pledge_end_date) {
      const startDate = new Date(formData.pledge_start_date);
      const endDate = new Date(formData.pledge_end_date);
      if (endDate < startDate) {
        errors.pledge_end_date = 'End date must be after start date';
      }
    }
  }

  if (collected_amount > formData.amount) {
    errors.amount = 'pledge Amount cannot exceed collected amount';
  }

  if (formData.amount < collected_amount && formData.pledge_status === 'CLOSED') {
    errors.amount = 'Pledge Amount cannot be less than collected amount';
  }

  if (formData.amount > collected_amount && formData.pledge_status === 'CLOSED') {
    errors.pledge_status =
      'Pledge status cannot be Closed as collected amount is less than pledged amount';
  }

  if (
    collected_amount > formData.amount &&
    (formData.pledge_status === 'PENDING' || formData.pledge_status === 'PAUSED')
  ) {
    errors.amount = 'Amount cannot exceed collected amount for status pending';
    errors.pledge_status =
      'Pledge status cannot be Pending or Paused if collected amount exceeding pledged amount';
  }

  return errors;
};

/**
 * Validate transaction form
 * @param {Object} formData - Form data to validate
 * @param {boolean} isEditMode - Whether form is in edit mode
 * @returns {Object} errors - Object with field names as keys and error messages as values
 */
export const validateTransactionForm = (formData, isEditMode = false) => {
  const errors = {};

  // Contact validation (only in create mode)
  if (!isEditMode && !formData.contact_id) {
    errors.contact_id = 'Donor is required';
  }

  // Amount validation
  if (!formData.amount) {
    errors.amount = 'Amount is required';
  } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
    errors.amount = 'Amount must be a positive number';
  } else if (!Number.isInteger(parseFloat(formData.amount))) {
    errors.amount = 'Amount must be a whole number (no decimals)';
  } else if (parseFloat(formData.amount) > MAX_AMOUNT) {
    errors.amount = `Amount cannot exceed ₹50 crores (₹${MAX_AMOUNT.toLocaleString('en-IN')})`;
  }

  // Payment date validation
  if (!formData.payment_date) {
    errors.payment_date = 'Payment date is required';
  }

  // Payment method validation
  if (!formData.payment_method) {
    errors.payment_method = 'Payment method is required';
  }

  // Trust Master validation
  if (!formData.trust_master_id) {
    errors.trust_master_id = 'Trust is required';
  }

  // Bank details validation for specific payment methods
  if (formData.payment_method === 'BANK_TRANSFER' || formData.payment_method === 'CHEQUE') {
    if (!formData.bank_name) {
      errors.bank_name = 'Bank name is required for this payment method';
    }
    if (!formData.bank_account_number) {
      errors.bank_account_number = 'Bank account number is required for this payment method';
    }
  }

  // Transaction reference validation for cheque
  if (formData.payment_method === 'CHEQUE' && !formData.transaction_reference) {
    errors.transaction_reference = 'Cheque number is required';
  }

  return errors;
};

/**
 * Validate reminder form
 * @param {Object} formData - Form data to validate
 * @returns {Object} errors - Object with field names as keys and error messages as values
 */
export const validateReminderForm = (formData) => {
  const errors = {};

  // Contact validation
  if (!formData.contact_id) {
    errors.contact_id = 'Contact is required';
  }

  // Donation validation
  if (!formData.donation_id) {
    errors.donation_id = 'Pledge is required';
  }

  // Amount validation
  if (!formData.amount) {
    errors.amount = 'Amount is required';
  } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
    errors.amount = 'Amount must be a positive number';
  } else if (!Number.isInteger(parseFloat(formData.amount))) {
    errors.amount = 'Amount must be a whole number (no decimals)';
  }

  // Reminder date validation
  if (!formData.reminder_date) {
    errors.reminder_date = 'Reminder date is required';
  }

  return errors;
};

/**
 * Validate trust master form
 * @param {Object} formData - Form data to validate
 * @returns {Object} errors - Object with field names as keys and error messages as values
 */
export const validateTrustMasterForm = (formData) => {
  const errors = {};

  // Trust name validation (required)
  if (!formData.trustName || formData.trustName.trim() === '') {
    errors.trustName = 'Trust name is required';
  } else if (formData.trustName.length > 100) {
    errors.trustName = 'Trust name cannot exceed 100 characters';
  }

  // Receipt prefix validation (optional)
  if (formData.receiptPrefix) {
    if (!/^[A-Za-z0-9]*$/.test(formData.receiptPrefix)) {
      errors.receiptPrefix = 'Receipt prefix must contain only alphanumeric characters';
    } else if (formData.receiptPrefix.length > 5) {
      errors.receiptPrefix = 'Receipt prefix cannot exceed 5 characters';
    }
  }

  // Notes validation (optional)
  if (formData.notes && formData.notes.length > 2000) {
    errors.notes = 'Notes cannot exceed 2000 characters';
  }

  // Footer text validation (optional)
  if (formData.footerText && formData.footerText.length > 300) {
    errors.footerText = 'Footer text cannot exceed 300 characters';
  }

  return errors;
};

/**
 * Parse backend validation errors into field-level errors
 * @param {Object} error - Error object from backend
 * @returns {Object} errors - Object with field names as keys and error messages as values
 */
export const parseBackendErrors = (error) => {
  const errors = {};

  // Handle validation errors from backend
  if (error?.error?.errors && Array.isArray(error.error.errors)) {
    error.error.errors.forEach((err) => {
      if (err.field) {
        errors[err.field] = err.message || 'Invalid value';
      }
    });
  }
  // Handle error with field property
  else if (error?.error?.field && error?.error?.message) {
    errors[error.error.field] = error.error.message;
  }
  // Handle single error message
  else if (error?.error?.message) {
    // Try to extract field name from message
    const message = error.error.message;

    // Common patterns in error messages
    if (message.includes('contact') || message.includes('donor')) {
      errors.contact_id = message;
    } else if (message.includes('amount') || message.includes('exceed')) {
      errors.amount = message;
    } else if (message.includes('date')) {
      errors.payment_date = message;
    } else if (message.includes('donation') || message.includes('pledge')) {
      errors.donation_id = message;
    } else if (message.includes('payment method')) {
      errors.payment_method = message;
    } else {
      // Generic error - show at top of form
      errors._general = message;
    }
  }
  // Handle string error
  else if (typeof error === 'string') {
    errors._general = error;
  }
  // Handle error message property
  else if (error?.message) {
    errors._general = error.message;
  }

  return errors;
};

/**
 * Check if form has any errors
 * @param {Object} errors - Errors object
 * @returns {boolean} - True if there are errors
 */
export const hasErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

/**
 * Get error message for a field
 * @param {Object} errors - Errors object
 * @param {string} fieldName - Field name
 * @returns {string} - Error message or empty string
 */
export const getFieldError = (errors, fieldName) => {
  return errors[fieldName] || '';
};

/**
 * Clear error for a specific field
 * @param {Object} errors - Current errors object
 * @param {string} fieldName - Field name to clear
 * @returns {Object} - New errors object without the specified field
 */
export const clearFieldError = (errors, fieldName) => {
  const newErrors = { ...errors };
  delete newErrors[fieldName];
  return newErrors;
};
